import React, { useMemo } from 'react';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import {
	PaginationControlsWrapper,
	PaginationButtonContainer,
	PaginationNumber,
	Shadow,
} from './elements';

const WINDOW_SIZE = 6;

interface PaginationControlsProps {
	isLoading: boolean;
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
}

export default function PaginationControls(props: PaginationControlsProps) {
	const {
		isLoading,
		currentPage,
		totalPages,
		onPageChange,
	} = props;

	const showFirstThree = useMemo(() => currentPage <= 3, [currentPage]);
	const showLastThree = useMemo(() => currentPage >= totalPages - 3, [currentPage, totalPages]);

	const numberButtons = useMemo(() => {
		let startPage = Math.max(1, currentPage - 3);
		let endPage = Math.min(totalPages, currentPage + 2);

		// first 3 pages
		if (showFirstThree) {
			startPage = 1;
			endPage = WINDOW_SIZE;
		}

		// last 3 pages
		if (showLastThree) {
			startPage = Math.max(1, totalPages - WINDOW_SIZE);
			endPage = totalPages;
		}

		const elements = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
		return (
			<>
				{elements.map((page) => (
					<PaginationButtonContainer
						key={page}
						className={currentPage === page ? 'selected' : ''}
						onClick={() => onPageChange(page)}
						aria-disabled={isLoading}
					>
						<PaginationNumber>{page}</PaginationNumber>
					</PaginationButtonContainer>
				))}
			</>
		);
	}, [currentPage, totalPages, showFirstThree, showLastThree, onPageChange]);

	return totalPages > 1 ? (
		<PaginationControlsWrapper className={isLoading ? 'searching' : ''}>
			<Shadow className="pagination" />
			{currentPage > 1 && (
				<PaginationButtonContainer onClick={() => onPageChange(currentPage - 1)}>
					<Icon name={ICONS.ARROW_LEFT} size={12} color={COLORS.TEXT_GRAY_300} />
				</PaginationButtonContainer>
			)}
			{numberButtons}
			{!showLastThree && (
				<>
					<PaginationButtonContainer className="ellipses">
						<Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={12} color={COLORS.TEXT_GRAY_300} />
					</PaginationButtonContainer>
					<PaginationButtonContainer onClick={() => onPageChange(totalPages)}>
						<PaginationNumber>{totalPages}</PaginationNumber>
					</PaginationButtonContainer>
				</>
			)}
			{currentPage < totalPages && (
				<PaginationButtonContainer onClick={() => onPageChange(currentPage + 1)}>
					<Icon name={ICONS.ARROW_RIGHT} size={12} color={COLORS.TEXT_GRAY_300} />
				</PaginationButtonContainer>
			)}
		</PaginationControlsWrapper>
	) : (
		<Shadow />
	);
}
